import DateRangeWithShortcuts from '@/components/DateRangeWithShortcuts';
import NumberInput from '@/components/NumberInput';
import { getFieldErrorProps } from '@/helpers/form';
import { useAppSelector } from '@/store/hooks';
import { selectShowBidCpmControl } from '@/store/selectors/currentWhitelabel.selectors';
import { FormLabel, TextField } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Moment } from 'moment/moment';
import { useCallback } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

interface IProps {
  index: number;
}

const DV360TicketsLineItemFormBasicInfo = ({ index }: IProps) => {
  const { control, setValue } = useFormContext();
  const showBidCpm = useAppSelector(selectShowBidCpmControl);

  const [watchStartDate, watchEndDate] = useWatch({
    control,
    name: ['startDate', 'endDate'],
  });

  const handleDatesChange = useCallback(
    ([startDate, endDate]: DateRange<Moment>) => {
      setValue('startDate', startDate || null, { shouldDirty: true, shouldValidate: true });
      setValue('endDate', endDate || null, { shouldDirty: true, shouldValidate: true });
    },
    [setValue],
  );

  return (
    <div className='flex flex-col w-full'>
      <div className='flex justify-between flex-row space-x-6'>
        <div className='flex flex-col w-full'>
          <FormLabel htmlFor={`line-item-name-${index}`} aria-label='Line Item Name' required>
            Line Item Name
          </FormLabel>
          <Controller
            name='name'
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id={`line-item-name-${index}`}
                fullWidth
                placeholder='Enter Line Item Name'
                {...getFieldErrorProps(fieldState)}
              />
            )}
          />
        </div>
        <div className='flex flex-col w-full'>
          <FormLabel htmlFor={`budget-${index}`} aria-label='Wholesale Budget' required>
            Wholesale Budget
          </FormLabel>
          <Controller
            name='budget'
            control={control}
            render={({ field, fieldState }) => (
              <NumberInput
                {...field}
                // @ts-expect-error jsx component prop
                id={`budget-${index}`}
                fullWidth
                placeholder='Enter Budget'
                disableInputArrows
                maximumFractionDigits={2}
                NumberInputProps={{
                  thousandsSeparator: ',',
                  unmask: true,
                }}
                {...getFieldErrorProps(fieldState)}
              />
            )}
          />
        </div>
        {showBidCpm && (
          <div className='flex flex-col w-full'>
            <FormLabel htmlFor={`bid-cpm-${index}`} aria-label='Bid CPM' required>
              Bid CPM
            </FormLabel>
            <Controller
              name='bidCpm'
              control={control}
              render={({ field, fieldState }) => (
                <NumberInput
                  {...field}
                  // @ts-expect-error jsx component prop
                  id={`bid-cpm-${index}`}
                  fullWidth
                  placeholder='Enter Bid CPM'
                  disableInputArrows
                  maximumFractionDigits={2}
                  NumberInputProps={{
                    thousandsSeparator: ',',
                    unmask: true,
                  }}
                  {...getFieldErrorProps(fieldState)}
                />
              )}
            />
          </div>
        )}
      </div>
      <div className='flex justify-between flex-row space-x-6 mt-2'>
        <div className='flex flex-col w-full'>
          <FormLabel htmlFor={`date-range-${index}`} aria-label='Date Range' required>
            Date Range
          </FormLabel>
          <DateRangeWithShortcuts
            value={[watchStartDate, watchEndDate]}
            onChange={handleDatesChange}
            timezone='UTC'
            disablePast
            inputProps={{
              InputProps: {
                id: `date-range-${index}`,
                placeholder: 'Select Date Range',
                startAdornment: null,
                sx: {
                  '& input': {
                    paddingRight: 0,
                  },
                  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'inherit',
                  },
                },
              },
            }}
          />
        </div>
        <div className='flex flex-col w-full'>
          <FormLabel htmlFor={`line-item-alt-id-${index}`} aria-label='External ID / Billing ID'>
            External ID / Billing ID
          </FormLabel>
          <Controller
            name='altId'
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id={`line-item-alt-id-${index}`}
                fullWidth
                placeholder='Enter External ID / Billing ID'
                {...getFieldErrorProps(fieldState)}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default DV360TicketsLineItemFormBasicInfo;
